/**
 * @fileoverview Input/output schemas for character-snapshotter-single-v2.
 *
 * This agent analyzes a chapter to extract events for a single character.
 *
 * Output schema is flattened (no wrapper object) to reduce JSON schema nesting
 * depth for Gemini API compatibility. majorCharacterEvents is at the top level
 * rather than nested inside stateSnapshot.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterAttributesSchema } from '@/domains/characters/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeCharacterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeCharacterCurrentContext.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the character snapshotter agent.
 *
 * Flattened structure to reduce nesting depth (3 levels max):
 * - Level 1: Root object
 * - Level 2: stateSnapshot object / majorCharacterEvents array
 * - Level 3: majorCharacterEvents items
 *
 * This avoids Gemini's "maximum allowed nesting depth" error.
 */
export const OutputSchema = z.object({
  characterName: z.string().describe("The name of the character this analysis pertains to."),
  stateSnapshot: CharacterAttributesSchema.describe(
    "A synthesized snapshot of the character's state at the conclusion of the chapter."
  ),
  majorCharacterEvents: z.array(z.object({
    id: z.string().describe("Unique identifier for the event."),
    description: z.string().describe("Description of a major character event."),
  })).optional().describe("A list of key events that happened to this character within this chapter."),
});

export type Output = z.infer<typeof OutputSchema>;
